import React, { useState, useEffect } from 'react'
import { useNotifications } from '../contexts/NotificationContext'

const NotificationToast = () => {
  const { notifications, dismissNotification, markAsRead, toastsEnabled } = useNotifications()
  const [visibleToasts, setVisibleToasts] = useState([])
  const [dismissingToasts, setDismissingToasts] = useState(new Set())

  useEffect(() => {
    // Show only unread notifications as toasts
    const unreadNotifications = notifications.filter(n => !n.read && !n.dismissed)
    setVisibleToasts(unreadNotifications.slice(0, 3)) // Show max 3 toasts
  }, [notifications])

  const handleDismiss = (id) => {
    // Add to dismissing set for fade-out animation
    setDismissingToasts(prev => new Set([...prev, id]))
    
    // Actually dismiss after animation completes
    setTimeout(() => {
      dismissNotification(id)
      setDismissingToasts(prev => {
        const newSet = new Set(prev)
        newSet.delete(id)
        return newSet
      })
    }, 300)
  }

  const handleMarkAsRead = (id) => {
    markAsRead(id)
  }

  const getPriorityIcon = (priority) => {
    switch (priority) {
      case 'critical': return 'fas fa-exclamation-triangle'
      case 'warning': return 'fas fa-exclamation-circle'
      case 'info': return 'fas fa-info-circle'
      case 'success': return 'fas fa-check-circle'
      default: return 'fas fa-bell'
    }
  }

  const getPriorityClass = (priority) => {
    switch (priority) {
      case 'critical': return 'alert-danger'
      case 'warning': return 'alert-warning'
      case 'info': return 'alert-info'
      case 'success': return 'alert-success'
      default: return 'alert-primary'
    }
  }

  // Don't show toasts if user has disabled them
  if (!toastsEnabled || visibleToasts.length === 0) return null

  return (
    <div className="notification-toast-container" style={{
      position: 'fixed',
      top: '20px',
      right: '20px',
      zIndex: 9999,
      maxWidth: '400px'
    }}>
      {visibleToasts.map((notification, index) => (
        <div
          key={notification.id}
          className={`alert ${getPriorityClass(notification.priority)} alert-dismissible fade show mb-2 shadow-sm ${
            dismissingToasts.has(notification.id) ? 'dismissing' : ''
          }`}
          style={{
            animationName: dismissingToasts.has(notification.id) 
              ? 'slideOutRight' 
              : 'slideInRight',
            animationDuration: '0.3s',
            animationTimingFunction: dismissingToasts.has(notification.id) 
              ? 'ease-in' 
              : 'ease-out',
            animationFillMode: dismissingToasts.has(notification.id) 
              ? 'forwards' 
              : 'none',
            animationDelay: dismissingToasts.has(notification.id) 
              ? '0s' 
              : `${Math.min(index * 0.1, 0.3)}s`,
            position: 'relative',
            zIndex: 10000 - index // Ensure proper stacking
          }}
        >
          <div className="d-flex align-items-start">
            <div className="me-2 mt-1">
              <i className={getPriorityIcon(notification.priority)}></i>
            </div>
            <div 
              className="flex-grow-1" 
              style={{ cursor: 'pointer' }}
              onClick={() => handleMarkAsRead(notification.id)}
              title="Click to mark as read"
            >
              <div className="fw-bold small">{notification.title}</div>
              <div className="small">{notification.message}</div>
              {notification.deviceId && (
                <div className="text-muted" style={{ fontSize: '0.75rem' }}>
                  Device: {notification.deviceId}
                </div>
              )}
            </div>
            <button
              type="button"
              className="btn-close btn-sm"
              onClick={(e) => {
                e.stopPropagation()
                e.preventDefault()
                if (!dismissingToasts.has(notification.id)) {
                  handleDismiss(notification.id)
                }
              }}
              disabled={dismissingToasts.has(notification.id)}
              title="Close alert"
              aria-label="Close"
              style={{
                position: 'relative',
                zIndex: 10001,
                pointerEvents: dismissingToasts.has(notification.id) ? 'none' : 'auto'
              }}
            ></button>
          </div>
          <div className="text-muted" style={{ fontSize: '0.7rem', marginTop: '4px' }}>
            {new Date(notification.timestamp).toLocaleTimeString()}
          </div>
        </div>
      ))}
      
    </div>
  )
}

export default NotificationToast